<template>
  <div class="address-autocomplete-wrapper">
    <label v-if="label" :for="inputId" class="block text-sm font-medium text-gray-700">
      {{ label }}
    </label>
    <input
      :id="inputId"
      ref="autocompleteInput"
      type="text"
      :value="modelValue"
      @input="$emit('update:modelValue', $event.target.value)"
      :placeholder="placeholder"
      :class="inputClass"
      class="w-full block mx-auto sm:text-sm"
    />
  </div>
</template>

<script>
export default {
  name: 'AddressAutocomplete',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Enter location...'
    },
    label: {
      type: String,
      default: ''
    },
    inputId: {
      type: String,
      default: 'address-autocomplete'
    },
    inputClass: {
      type: String,
      default: ''
    },
    types: {
      type: Array,
      default: () => ['(cities)'] // Default to cities, can also be ['geocode'], ['establishment'], etc.
    },
    componentRestrictions: {
      type: Object,
      default: () => ({ country: 'us' }) // Restrict to US by default
    },
    requireSelection: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue', 'place-selected', 'place-changed'],
  data() {
    return {
      autocomplete: null,
      isGoogleMapsLoaded: false,
      hasSelectedPlace: false
    };
  },
  mounted() {
    this.initializeAutocomplete();
  },
  beforeUnmount() {
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
    }
  },
  methods: {
    initializeAutocomplete() {
      // Check if Google Maps API + Places library is fully loaded
      const isReady = () =>
        typeof google !== 'undefined' &&
        typeof google.maps !== 'undefined' &&
        typeof google.maps.places !== 'undefined' &&
        typeof google.maps.places.Autocomplete === 'function';

      if (!isReady()) {
        console.warn('Google Maps Places API not ready. Waiting...');
        let attempts = 0;
        const maxAttempts = 100; // 10 seconds max
        const checkGoogleMaps = () => {
          if (isReady()) {
            this.setupAutocomplete();
          } else if (attempts++ < maxAttempts) {
            setTimeout(checkGoogleMaps, 100);
          } else {
            console.error('Google Maps Places API failed to load after 10s');
          }
        };
        checkGoogleMaps();
        return;
      }
      
      this.setupAutocomplete();
    },
    
    setupAutocomplete() {
      try {
        // Create the autocomplete object
        this.autocomplete = new google.maps.places.Autocomplete(
          this.$refs.autocompleteInput,
          {
            types: this.types,
            componentRestrictions: this.componentRestrictions
          }
        );

        // Listen for place selection
        this.autocomplete.addListener('place_changed', () => {
          const place = this.autocomplete.getPlace();
          this.hasSelectedPlace = true;
          this.handlePlaceSelection(place);
        });
        
        // When user types, mark as not yet confirmed
        this.$refs.autocompleteInput.addEventListener('input', () => {
          this.hasSelectedPlace = false;
        });
        // On blur, if selection is required and user didn't pick a suggestion, try to validate
        this.$refs.autocompleteInput.addEventListener('blur', () => {
          if (this.requireSelection && this.$refs.autocompleteInput.value && !this.hasSelectedPlace) {
            const guess = this.tryParseAddress(this.$refs.autocompleteInput.value);
            if (guess.city && guess.state) {
              // If global helpers exist, show confirm modal similar to campaign form
              if (window.campaignAddressHelpers && window.campaignAddressHelpers.showLocationConfirmModal) {
                window.campaignAddressHelpers.showLocationConfirmModal(
                  guess.city,
                  guess.state,
                  () => {
                    // Confirm: accept parsed value as valid
                    this.hasSelectedPlace = true;
                    this.$emit('place-selected', { place: { name: this.$refs.autocompleteInput.value }, components: guess });
                  },
                  () => {
                    // Edit: focus back
                    this.$refs.autocompleteInput.focus();
                  },
                  this.$refs.autocompleteInput.value
                );
              }
            }
          }
        });
        
        this.isGoogleMapsLoaded = true;
      } catch (error) {
        console.error('Error initializing Google Places Autocomplete:', error);
      }
    },
    
    handlePlaceSelection(place) {
      if (!place.geometry) {
        console.warn('No details available for input: ' + place.name);
        return;
      }

      // Extract address components
      const addressComponents = this.parseAddressComponents(place.address_components);
      
      // Update the input value
      this.$emit('update:modelValue', place.name);
      
      // Emit detailed place information
      this.$emit('place-selected', {
        place: place,
        formatted_address: place.formatted_address,
        components: addressComponents,
        geometry: place.geometry
      });
      
      // Also emit the place-changed event for backward compatibility
      this.$emit('place-changed', place);
    },
    
    parseAddressComponents(components) {
      const result = {};
      
      components.forEach(component => {
        const types = component.types;
        
        if (types.includes('locality')) {
          result.city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          result.state = component.short_name;
          result.stateLong = component.long_name;
        }
        if (types.includes('postal_code')) {
          result.zip = component.long_name;
        }
        if (types.includes('country')) {
          result.country = component.short_name;
          result.countryLong = component.long_name;
        }
        if (types.includes('street_number')) {
          result.streetNumber = component.long_name;
        }
        if (types.includes('route')) {
          result.streetName = component.long_name;
        }
      });
      
      // Create full street address if components are available
      if (result.streetNumber && result.streetName) {
        result.streetAddress = `${result.streetNumber} ${result.streetName}`;
      }
      
      return result;
    },
    
    // Lightweight parser for "City, ST [ZIP]" patterns
    tryParseAddress(value) {
      const patterns = [
        /,\s*([^,]+),\s*([A-Z]{2})(?:\s+\d{5})?/i,   // "123 Main, Coral Springs, FL 33065"
        /^([^,]+),\s*([A-Z]{2})(?:\s+\d{5})?$/i      // "Coral Springs, FL 33065"
      ];
      for (const re of patterns) {
        const m = value.match(re);
        if (m) {
          return { city: m[1].trim(), state: m[2].toUpperCase() };
        }
      }
      return {};
    },

    // Method to clear the input
    clearInput() {
      this.$emit('update:modelValue', '');
      if (this.$refs.autocompleteInput) {
        this.$refs.autocompleteInput.value = '';
      }
    },
    
    // Method to set input value programmatically
    setValue(value) {
      this.$emit('update:modelValue', value);
      if (this.$refs.autocompleteInput) {
        this.$refs.autocompleteInput.value = value;
      }
    }
  }
};
</script>

<style scoped>
.address-autocomplete-wrapper {
  position: relative;
}

/* Style the Google Places dropdown */
.pac-container {
  border-radius: 8px;
  border: 1px solid #d1d5db;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.pac-item {
  padding: 8px 12px;
  cursor: pointer;
}

.pac-item:hover {
  background-color: #f3f4f6;
}

.pac-item-selected {
  background-color: #e5e7eb;
}

.pac-item-query {
  font-weight: 600;
  color: #111827;
}
</style>
